import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import LeaderTileStrip from '../LeaderTileStrip';
import WorkshopsUserCourseProgressTable from './WorkshopsUserCourseProgressTable';
import WorkshopsUserEventsTable from '../../Common/MoodleEventBooking/WorkshopsUserEventsTable';
import StbAccordion from '../../security-toolbox-ui/StbAccordion';

import { useFetchDashboardPlacements, useFetchDashboardTileLinks } from '../../../hooks/toolbox-hooks';
import type { DashboardPlacement, DashboardTileLink } from '../../../types/dashboard';
import { TileRuntimeContext } from '../../../support/tileRuntimeContext';

interface Props {
    email: string;
}

const COMPONENT_KEY_WORKSHOPS = 'Leader.WorkshopsIndividualContributorPage';
const REGION_KEY_OVERVIEW = 'overview';

export default function WorkshopsIndividualContributorPage({ email }: Props) {
    const [isOverviewOpen, setIsOverviewOpen] = useState(true);
    const [isProgressOpen, setIsProgressOpen] = useState(true);
    const [isEventsOpen, setIsEventsOpen] = useState(true);

    const runtimeContext: TileRuntimeContext = useMemo(() => {
        return {
            email,
            userEmail: email,
            domain: 'workshops',
            teamScope: [email],
            teamEmailsList: email,
            teamSize: 1,
            viewMode: 'individual',
            isTeamView: false,
            isIndividualView: true,
            dataSource: 'workshops',
            perspective: 'individual_contributor',
        };
    }, [email]);

    const {
        data: placements,
        isLoading: isLoadingPlacements,
        error: placementsError,
    } = useFetchDashboardPlacements(
      { component_key: COMPONENT_KEY_WORKSHOPS, region_key: REGION_KEY_OVERVIEW, enabled: true },
      { placeholderData: keepPreviousData, refetchOnWindowFocus: false }
    );

    const placement: DashboardPlacement | null = useMemo(() => {
        const arr = Array.isArray(placements) ? placements : [];
        return arr.length ? arr[0] : null;
    }, [placements]);

    const {
        data: tileLinks,
        isLoading: isLoadingLinks,
        error: linksError,
    } = useFetchDashboardTileLinks(
      placement?.id ? { placement: placement.id, enabled: true, order_by: 'order' } : undefined,
      { enabled: !!placement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false }
    );

    const orderedLinks = useMemo(() => {
        const links = (Array.isArray(tileLinks) ? tileLinks : []) as DashboardTileLink[];
        return links
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id);
    }, [tileLinks]);

    const overviewLinks = useMemo(() => {
        return orderedLinks.filter((link) => !!link?.tile?.name);
    }, [orderedLinks]);

    const hasOverviewTiles = !!placement && overviewLinks.length > 0;

    if (placementsError) {
        return <div style={{ padding: 12, color: 'red' }}>{(placementsError as Error).message}</div>;
    }

    if (linksError) {
        return <div style={{ padding: 12, color: 'red' }}>{(linksError as Error).message}</div>;
    }

    const columns = 3;

    return (
      <div className="flex flex-col gap-4">
          {(isLoadingPlacements || isLoadingLinks) ? (
            <div style={{ padding: 12 }}>
                <HbrSpinner>Loading overview…</HbrSpinner>
            </div>
          ) : hasOverviewTiles ? (
            <StbAccordion
              variant="minimal"
              label="Workshops – Individual Overview"
              isOpen={isOverviewOpen}
              toggleIsOpen={setIsOverviewOpen}
            >
                <div style={{ minHeight: 220, padding: 12 }} className="flex flex-col gap-3">
                    <div
                      className="grid gap-3"
                      style={{
                          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                      }}
                    >
                        {overviewLinks.map((link) => {
                            const tileName = link?.tile?.name;
                            if (!tileName) return null;

                            const colSpan = Number((link.options as any)?.colSpan ?? 1);
                            const minHeight = (link.options as any)?.minHeight ?? 120;

                            return (
                              <div
                                key={`overview-tile-${link.id}`}
                                className="min-w-0 flex flex-col"
                                style={{
                                    minHeight,
                                    gridColumn: `span ${Math.max(1, Math.min(columns, colSpan))}`,
                                }}
                              >
                                  <LeaderTileStrip
                                    tileNames={[tileName]}
                                    runtimeContext={runtimeContext}
                                    className="h-full min-w-0"
                                  />
                              </div>
                            );
                        })}
                    </div>
                </div>
            </StbAccordion>
          ) : null}

          <StbAccordion
            variant="minimal"
            label="Course Progress & TTT Readiness"
            isOpen={isProgressOpen}
            toggleIsOpen={setIsProgressOpen}
          >
              <div style={{ height: 700, minHeight: 0, padding: 12 }}>
                  <WorkshopsUserCourseProgressTable email={email} />
              </div>
          </StbAccordion>

          <StbAccordion
            variant="minimal"
            label="Events & Feedback"
            isOpen={isEventsOpen}
            toggleIsOpen={setIsEventsOpen}
          >
              <div style={{ height: 700, minHeight: 0, padding: 12 }}>
                  <WorkshopsUserEventsTable email={email} />
              </div>
          </StbAccordion>
      </div>
    );
}